import { formatDateLocal } from '@/lib/timeFormatUtils';
import { NoticeScope } from '@/lib/establishmentNoticeUtils';

export type QuestionScope = NoticeScope;

export interface EstablishmentQuestion {
  id: string;
  establishmentId: string;
  question: string;
  options: string[];
  allowMultiple: boolean;
  isRequired: boolean;
  scope: QuestionScope;
  specificDate: string | null; // yyyy-MM-dd
  weekday: number | null; // 0 = dimanche … 6 = samedi
  isActive: boolean;
}

/** Réponse enregistrée sur la réservation. */
export interface ReservationCustomAnswers {
  question: string;
  answers: string[];
}

export const QUESTION_MAX_LENGTH = 200;
export const QUESTION_OPTION_MAX_LENGTH = 100;
export const QUESTION_MAX_OPTIONS = 5;

/**
 * Sélectionne la question à poser pour une date donnée.
 * Priorité : date précise > jour de semaine > permanent.
 * Les questions inactives, sans texte ou sans option sont ignorées.
 */
export function selectApplicableQuestion(
  questions: EstablishmentQuestion[] | undefined | null,
  date: Date | undefined | null
): EstablishmentQuestion | null {
  if (!questions || questions.length === 0) return null;
  const active = questions.filter(
    (q) =>
      q.isActive &&
      q.question.trim() !== '' &&
      Array.isArray(q.options) &&
      q.options.filter((o) => o.trim() !== '').length > 0
  );
  if (active.length === 0) return null;

  if (date) {
    const iso = formatDateLocal(date);
    const exact = active.find((q) => q.scope === 'date' && q.specificDate === iso);
    if (exact) return exact;

    const weekday = date.getDay();
    const recurring = active.find((q) => q.scope === 'weekday' && q.weekday === weekday);
    if (recurring) return recurring;
  }

  return active.find((q) => q.scope === 'permanent') ?? null;
}

/**
 * Normalise une réponse client : ne conserve que des options réellement proposées,
 * et une seule réponse si la question n'accepte pas les réponses multiples.
 */
export function normalizeAnswers(
  question: EstablishmentQuestion,
  answers: string[] | undefined | null
): string[] {
  const valid = (answers ?? []).filter((a) => question.options.includes(a));
  const unique = Array.from(new Set(valid));
  return question.allowMultiple ? unique.slice(0, QUESTION_MAX_OPTIONS) : unique.slice(0, 1);
}
